import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, ExternalLink, Plus, Loader2, Globe, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { showError } from "@/lib/errors";

/**
 * Bouton + dialog de recherche IA de sources publiques pour une fiche.
 *
 * Recherche sur Tahiti Infos, TNTV, Radio 1, Polynésie La 1ère, Wikipedia FR.
 *
 * Aucune publication automatique : l'utilisateur valide chaque source à ajouter.
 *
 * Props :
 *  - entity : { first_name, last_name, nickname?, discipline?, club?, entity_type? }
 *  - onAddSource(candidate) : callback appelé pour chaque source ajoutée (le parent gère la persistance)
 *  - label  : texte du bouton (défaut: "Rechercher automatiquement des sources publiques")
 *  - variant: variant du Button
 *  - size   : taille du Button
 *  - dataTestId : test id du bouton
 */
export function AISourceSearchButton({
  entity,
  onAddSource,
  label = "Rechercher automatiquement des sources publiques",
  variant = "outline",
  size = "sm",
  dataTestId = "ai-find-sources-btn",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [queries, setQueries] = useState([]);
  const [added, setAdded] = useState(new Set());

  const run = async () => {
    if (!entity?.first_name && !entity?.last_name && !entity?.nickname) {
      toast.error("Renseigne au moins prénom + nom (ou un surnom) avant de lancer la recherche.");
      return;
    }
    setLoading(true);
    setCandidates(null);
    try {
      const { data } = await api.post("/ai/find-sources", entity);
      setCandidates(data.candidates || []);
      setQueries(data.queries_tried || []);
      if (!(data.candidates || []).length) {
        toast.info("Aucune source publique trouvée pour cette identité.");
      }
    } catch (e) {
      showError(e, { prefix: "Recherche IA :" });
      setCandidates([]);
    } finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(true);
    if (candidates === null) run();
  };

  const handleAdd = (cand) => {
    onAddSource?.(cand);
    setAdded((s) => new Set(s).add(cand.url));
    toast.success("Source ajoutée à la fiche.");
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={handleOpen} data-testid={dataTestId}>
        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="ai-find-sources-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
              Recherche IA de sources publiques
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-start gap-2 text-xs p-3 rounded border bg-muted/40">
              <AlertCircle className="h-4 w-4 text-[hsl(var(--aito-lagoon))] shrink-0 mt-0.5" />
              <p>
                Les sources sont uniquement <strong>proposées</strong>. Aucune publication automatique :
                tu choisis quelles URLs ajouter à la fiche. Médias scannés : Tahiti Infos, TNTV, Radio 1, Polynésie La 1ère, Wikipedia FR.
              </p>
            </div>

            {queries.length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                Requêtes testées :{" "}
                {queries.map((q, i) => <Badge key={i} variant="outline" className="text-[10px] mr-1">{q}</Badge>)}
              </div>
            )}

            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : candidates && candidates.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground italic">
                Aucune source publique trouvée — essaie d'ajouter un surnom ou de préciser la discipline.
              </div>
            ) : (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="ai-find-sources-results">
                {(candidates || []).map((c, i) => {
                  const isAdded = added.has(c.url);
                  return (
                    <Card key={i} className="p-3 flex gap-3 items-start" data-testid={`ai-source-candidate-${i}`}>
                      {c.image ? (
                        <img src={c.image} alt="" className="h-16 w-16 object-cover rounded shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <div className="h-16 w-16 rounded bg-muted flex items-center justify-center shrink-0">
                          <Globe className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                          <Badge variant="secondary" className="text-[10px]">{c.media}</Badge>
                          <Badge variant="outline" className="text-[10px]">
                            Confiance {Math.round((c.confidence || 0) * 100)}%
                          </Badge>
                          {c.date && <span className="text-[10px] text-muted-foreground">{c.date}</span>}
                        </div>
                        <a href={c.url} target="_blank" rel="noreferrer" className="font-display font-semibold text-sm leading-tight hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 block">
                          {c.title}
                        </a>
                        {c.excerpt && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.excerpt}</p>}
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5 hover:underline mt-1">
                          <ExternalLink className="h-2.5 w-2.5" />
                          {c.url}
                        </a>
                      </div>
                      <Button
                        size="sm"
                        variant={isAdded ? "secondary" : "default"}
                        disabled={isAdded}
                        onClick={() => handleAdd(c)}
                        data-testid={`ai-source-add-${i}`}
                        className="shrink-0"
                      >
                        {isAdded ? "Ajoutée" : (<><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</>)}
                      </Button>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={run} disabled={loading} data-testid="ai-find-sources-refresh">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              Relancer la recherche
            </Button>
            <Button onClick={() => setOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
